import {
  Avatar,
  Button,
  Clickable,
  Col,
  Dropdown,
  Icon,
  Input,
  Row,
  ShoutoutPresets,
  Text,
  useShoutoutPresets
} from 'components';
import { theme } from 'lib';
import React from 'react';
import { useTranslation } from 'react-i18next';
import ReactPlayer from 'react-player';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { SearchUsers, UploadMedia } from '../../../components';
import { submit } from '../lib';

const Image = styled.img`
  width: 296px;
  height: 440px;
  object-fit: cover;
`;

const StyledReactPlayer = styled(ReactPlayer)`
  max-width: 296px;
  min-height: 440px;
`;

// Fills whatever column it's given (the right side of the target-picker's
// grid once a team is selected) rather than a fixed width.
const HeroBackground = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 32px 40px 40px;
  background: ${theme.TEAM_HERO_GRADIENT_COMPACT};
`;

const ShoutoutDetails = ({ data, setData, handleBack, handleClose, preSelected, handleNext }) => {
  const firstMedia =
      (data.media?.length && data.media[0].preview) ||
      data.metaData?.image ||
      (data.youtubeId && `https://img.youtube.com/vi/${data.youtubeId}/0.jpg`),
    video = data.media?.find((x) => x.file?.type?.includes('video')),
    dispatch = useDispatch(),
    { t } = useTranslation();

  // a game shout-out always resolves to one of its two teams via the dropdown
  // below, so it gets the same hero treatment as a direct team target
  const isTeamTarget = !!data.eventObject || data.entity?.type === 'team';

  // preset messages are a team-only feature, same as the mobile compose screen
  const {
    presets,
    fallback,
    loading: presetsLoading,
    refetch: refetchPresets,
    getPresetText
  } = useShoutoutPresets({
    teamId: isTeamTarget ? data.entity?._id : null,
    teamName: data.entity?.name,
    active: true
  });

  const selectedPresetId = data.presetId || null;
  const selectedPreset = presets.find((x) => x._id === selectedPresetId);
  const message = selectedPreset ? getPresetText(selectedPreset) : data.comment?.trim();

  function handleChange(opt) {
    setData((data) => ({
      ...data,
      ...opt
    }));
  }

  function handlePresetSelect(preset) {
    handleChange({ presetId: preset?._id || null, ...(preset && { comment: '' }) });
  }

  function handleComposerFocus() {
    handleChange({ presetId: null });
  }

  function handlePresetUnavailable() {
    handleChange({ presetId: null });
    refetchPresets();
  }

  async function handleSubmit() {
    if (data.eventObject || data.entity?.type === 'team') {
      return submit({
        dispatch,
        data: { ...data, comment: message },
        handleClose,
        onPresetUnavailable: handlePresetUnavailable
      });
    }

    return handleNext();
  }

  if (isTeamTarget) {
    return (
      <HeroBackground>
        {handleBack && (!preSelected || data?.eventObject) && (
          <Col noFlex>
            <Clickable onClick={handleBack}>
              <Col hasBorder="2px solid #fff" hasRadius="32px" wid="40px" ht="40px" centerAll>
                <Icon type="arrowLeft" dimensions={16} col="#fff" />
              </Col>
            </Clickable>
          </Col>
        )}

        {data.eventObject && (
          <Col wid="100%" marg="16px 0 0" bg="#fff" hasRadius="12px" pad="8px 12px 0" noFlex>
            <Dropdown
              title={t('common.team')}
              options={[data.eventObject.away, data.eventObject.home]}
              handleSelect={(opt) => handleChange({ entity: opt, presetId: null })}
              selected={data.entity?.name}
              contain
            />
          </Col>
        )}

        {!!data.entity?._id && (
          <Row centerAll noFlex wid="100%" marg="24px 0 0">
            <Avatar user={data.entity} showVerified />

            <Col marg="0 0 0 12px" center noFlex>
              <Text h2 col="#fff">
                {data.entity?.name}
              </Text>

              <Text c1 med col="#fff">
                @{data.entity?.username}
              </Text>
            </Col>
          </Row>
        )}

        <Row start wid="100%" noFlex marg="24px 0 0">
          <Text h4 col="#fff">
            {t('callToAction.yourShoutoutTo', { name: data.entity?.name })}
          </Text>
        </Row>

        <Col wid="100%" marg="8px 0 0" noFlex>
          <ShoutoutPresets
            dark
            presets={presets}
            fallback={fallback}
            loading={presetsLoading}
            getPresetText={getPresetText}
            selectedPresetId={selectedPresetId}
            onSelect={handlePresetSelect}
            teamName={data.entity?.name}
          />
        </Col>

        <Row start wid="100%" noFlex marg="24px 0 0">
          <Text h4 col="#fff">
            {t('callToAction.writeYourOwnMessage')}
          </Text>
        </Row>

        <Col wid="100%" noFlex onFocus={handleComposerFocus}>
          <Input
            marg="8px 0 0"
            wid="100%"
            bg="#fff"
            multiline
            emoji
            ht="160px"
            placeholder={t('callToAction.typeMessageOrEmojis')}
            value={data.comment}
            onChange={(e) => handleChange({ comment: e.target.value, presetId: null })}
          />
        </Col>

        <Button
          marg="16px 0 0"
          big
          wid="100%"
          bg={message ? theme.SECONDARY : theme.GREY_30}
          text={t('callToAction.postShoutout')}
          onClick={handleSubmit}
          disabled={!message || !data.entity?._id}
        />
      </HeroBackground>
    );
  }

  return (
    <React.Fragment>
      {handleBack && (!preSelected || data?.eventObject) && (
        <Col noFlex pos="absolute" top="24px" left="24px">
          <Clickable onClick={handleBack}>
            <Col hasBorder="2px solid #000" hasRadius="32px" wid="40px" ht="40px" centerAll>
              <Icon type="arrowLeft" dimensions={16} col={theme.GREY_60} />
            </Col>
          </Clickable>
        </Col>
      )}

      <Row wid="100%" marg="50px 0 0">
        {data.media?.length ? (
          <Col pos="relative" noFlex>
            <Col pos="absolute" top="16px" right="16px" zIndex={2}>
              <Clickable onClick={() => handleChange({ media: undefined })}>
                <Col
                  ht="32px"
                  wid="32px"
                  noFlex
                  hasRadius="32px"
                  bg="rgba(210, 216, 223, 0.7)"
                  centerAll
                >
                  <Icon type="close" dimensions={12} />
                </Col>
              </Clickable>
            </Col>

            {/* <StyledReactPlayer
              url={data.media[0].uri}
              controls
            /> */}
            {!!firstMedia && <Image src={firstMedia} />}

            {!!video && <StyledReactPlayer url={video.uri} controls />}
          </Col>
        ) : (
          <UploadMedia ht="440px" setData={setData} />
        )}

        <Col marg="0 0 0 24px" wid="100%">
          <SearchUsers
            title={t('common.player')}
            type="athlete"
            handleSelect={(opt) => handleChange({ entity: opt })}
            selected={data.entity?.name}
          />

          <Input
            marg="24px 0 0"
            multiline
            ht="88px"
            placeholder={t('common.typeYourMessage')}
            value={data.comment}
            onChange={(e) => handleChange({ comment: e.target.value })}
          />

          <Button
            marg="24px 0 0"
            wid="fit-content"
            pad="0 24px"
            text={t('common.next')}
            onClick={handleSubmit}
            disabled={!data.comment?.length || !data.entity?._id}
          />
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default ShoutoutDetails;
